import { faFloppyDisk, faPen, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Alert,
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  List,
  ListItem,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import dayjs from 'dayjs';
import { useAtomValue } from 'jotai';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import CheckPermissions from '@/components/CheckPermissions';
import LargeCard from '@/components/LargeCard';
import { PERMISSIONS } from '@/constants/auth.constants';
import { AI_ANALYST_NAME } from '@/constants/common';
import { AUTO_SOC_TIERS } from '@/constants/products.constants';
import useStyles from '@/pages/Administration/AdministrationGeneral/AdministrationGeneral.styles';
import { selectedOrganizationAtom } from '@/pages/Organizations/Organizations.store';
import { userIsInternalAtom } from '@/store/user.store';

import {
  AI_COMMUNICATION_LEVELS,
  type AIAnalystProps,
  type AICommunicationLevel,
  type AISettingsFormValues
} from './AIAnalyst.types';
import { useFetchAISettings, useMutateAISettings } from './useAISettings';

const AIAnalyst = ({ orgLabel }: AIAnalystProps) => {
  const { classes } = useStyles();
  const { data, isLoading } = useFetchAISettings(orgLabel);

  const mutation = useMutateAISettings(orgLabel);

  const [editing, isEditing] = useState<boolean>(false);
  const selectedOrganization = useAtomValue(selectedOrganizationAtom);
  const userIsInternal = useAtomValue(userIsInternalAtom);
  const isAutoSoc = selectedOrganization?.is_auto_soc > AUTO_SOC_TIERS.FORTIFY;

  const defaultFormValues: AISettingsFormValues = {
    ai_enable_submission: data.ai_enable_submission ?? false,
    ai_disable_anthropic_models: data.ai_disable_anthropic_models ?? false,
    ai_org_instructions: data.ai_org_instructions ?? '',
    ai_ticket_response_instructions: data.ai_ticket_response_instructions ?? '',
    ai_communication_level:
      (data.ai_communication_level as AICommunicationLevel) ?? 'default',
    message: ''
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, dirtyFields }
  } = useForm<AISettingsFormValues>({
    defaultValues: defaultFormValues,
    mode: 'onSubmit'
  });

  useEffect(() => {
    reset(defaultFormValues);
  }, [data]);

  const toggleChanged = !!dirtyFields.ai_enable_submission;

  const handleCancel = () => {
    isEditing(false);
    reset(defaultFormValues);
  };

  const onSubmit = (formValues: AISettingsFormValues) => {
    mutation.mutate(
      {
        ai_enable_submission: formValues.ai_enable_submission,
        ai_disable_anthropic_models: formValues.ai_disable_anthropic_models,
        ai_org_instructions: formValues.ai_org_instructions,
        ai_ticket_response_instructions:
          formValues.ai_ticket_response_instructions,
        ai_communication_level: formValues.ai_communication_level,
        message: toggleChanged ? formValues.message.trim() : null
      },
      {
        onSuccess: () => {
          isEditing(false);
          reset(formValues);
        }
      }
    );
  };

  return (
    <LargeCard
      title="General Configuration"
      rightContent={
        <CheckPermissions
          permissions={[PERMISSIONS.CASES_UPDATE, PERMISSIONS.INTERNAL]}
        >
          {editing ? (
            <Stack direction="row">
              <IconButton onClick={handleSubmit(onSubmit)}>
                <FontAwesomeIcon size="xs" icon={faFloppyDisk} />
              </IconButton>
              <IconButton onClick={handleCancel}>
                <FontAwesomeIcon size="xs" icon={faXmark} />
              </IconButton>
            </Stack>
          ) : (
            <IconButton onClick={() => isEditing(true)}>
              <FontAwesomeIcon size="xs" icon={faPen} />
            </IconButton>
          )}
        </CheckPermissions>
      }
    >
      <Stack direction="row" sx={{ alignItems: 'flex-start' }} spacing={4}>
        <Box sx={{ flex: 1 }}>
          {isAutoSoc && editing && (
            <Alert severity="info" sx={{ mb: 2 }}>
              AI submission is always enabled for Agentic-SOC organizations and
              cannot be disabled.
            </Alert>
          )}
          <Typography variant="h4">{AI_ANALYST_NAME} Submission</Typography>
          <Typography className={classes.subText} variant="body2">
            When enabled, cases can be submitted to <b>{AI_ANALYST_NAME}</b> for
            automated analysis.
          </Typography>

          <Controller
            name="ai_enable_submission"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    disabled={
                      !editing || isLoading || isAutoSoc || !userIsInternal
                    }
                    color="primary"
                    name="aiEnabled"
                  />
                }
                label="Enable submission for AI analysis"
              />
            )}
          />

          <Box sx={{ mt: 2 }}>
            <Controller
              name="ai_disable_anthropic_models"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      disabled={!editing || isLoading || !userIsInternal}
                      color="primary"
                      name="aiDisableAnthropicModels"
                    />
                  }
                  label={
                    <Box>
                      <Typography>Disable Anthropic Models</Typography>
                      <Typography className={classes.subText} variant="body2">
                        When enabled, <b>{AI_ANALYST_NAME}</b> will not use any
                        Anthropic models for its analysis.
                      </Typography>
                    </Box>
                  }
                />
              )}
            />
          </Box>

          {editing && toggleChanged && (
            <Box sx={{ mt: 1 }}>
              <InputLabel sx={{ mb: 1 }}>Reason for change</InputLabel>
              <Controller
                name="message"
                control={control}
                rules={{
                  validate: (value) =>
                    !toggleChanged || value?.trim()
                      ? true
                      : 'A reason is required when changing the AI submission toggle.'
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    placeholder="Explain why you are enabling or disabling AI submission…"
                    variant="outlined"
                    fullWidth
                    required
                    multiline
                    minRows={2}
                    error={!!errors.message}
                  />
                )}
              />
              {errors.message && (
                <FormHelperText error>{errors.message.message}</FormHelperText>
              )}
            </Box>
          )}
        </Box>

        {userIsInternal && data.recent_logs.length > 0 && (
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 600 }}>Recent changes</Typography>
            <List disablePadding dense>
              {data.recent_logs.map((log) => (
                <ListItem key={log.id} disablePadding sx={{ py: 0.5 }}>
                  <Box>
                    <Tooltip
                      title={dayjs
                        .utc(log.created_at)
                        .local()
                        .format('MMM D, YYYY h:mm A')}
                      placement="top"
                    >
                      <Typography variant="caption" color="text.secondary">
                        {dayjs.utc(log.created_at).local().fromNow()} &mdash;{' '}
                        <b>{log.user.name}</b>
                      </Typography>
                    </Tooltip>
                    <Typography variant="body2">{log.message}</Typography>
                  </Box>
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </Stack>

      <CheckPermissions permissions={[PERMISSIONS.INTERNAL]}>
        <Divider sx={{ my: 1.5 }} />
        <Typography variant="h4" sx={{ mb: 2 }}>
          {AI_ANALYST_NAME} Organization Instructions
        </Typography>

        <Stack spacing={1}>
          <Box>
            <InputLabel htmlFor="organizationInstructions">
              Security Analyst Agent Instructions
            </InputLabel>
            <Typography className={classes.subText} variant="body2">
              Instructions provided to <b>{AI_ANALYST_NAME}</b> when analyzing
              cases.
            </Typography>
            <Controller
              name="ai_org_instructions"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  id="organizationInstructions"
                  className={classes.textField}
                  variant="outlined"
                  disabled={!editing || isLoading}
                  fullWidth
                  type="text"
                  multiline
                />
              )}
            />
          </Box>

          <Box>
            <InputLabel htmlFor="ticketResponseInstructions">
              Ticket Response Agent Instructions
            </InputLabel>
            <Typography className={classes.subText} variant="body2">
              Instructions provided to <b>{AI_ANALYST_NAME}</b> when responding
              to ticket comments.
            </Typography>
            <Controller
              name="ai_ticket_response_instructions"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  id="ticketResponseInstructions"
                  className={classes.textField}
                  variant="outlined"
                  disabled={!editing || isLoading}
                  fullWidth
                  type="text"
                  multiline
                />
              )}
            />
          </Box>

          <Box>
            <InputLabel>Ticket Response Agent Communication Level</InputLabel>
            <Typography className={classes.subText} variant="body2">
              Level of communication used when <b>{AI_ANALYST_NAME}</b> responds
              to tickets.
            </Typography>
            <FormControl sx={{ ml: 2, pb: 2, mt: 1 }}>
              <Controller
                name="ai_communication_level"
                control={control}
                render={({ field }) => (
                  <RadioGroup {...field} row>
                    {AI_COMMUNICATION_LEVELS.map(({ value, label }) => (
                      <FormControlLabel
                        key={value}
                        value={value}
                        control={<Radio />}
                        label={label}
                        disabled={!editing || isLoading}
                        sx={{ mr: 6 }}
                      />
                    ))}
                  </RadioGroup>
                )}
              />
            </FormControl>
          </Box>
        </Stack>
      </CheckPermissions>
    </LargeCard>
  );
};

export default AIAnalyst;
